import BottomNav from './BottomNav'
import { IconHome } from './icons'

/**
 * Privacy Policy page.
 * Rendered as an in-app screen (via gameState 'privacy') and as a public URL
 * (/privacy) — when accessed via URL, onBack is undefined and the BottomNav
 * is hidden (same conditional as the old floating back arrow).
 */
export default function PrivacyPage({ onBack }) {
  return (
    <div className="legal-page">
      <div className="screen-content legal-page__content">
        <h1 className="legal-page__title">Privacy Policy</h1>
        <p className="legal-page__updated">Last Updated: May 2026</p>

        <p className="legal-page__intro">
          Your privacy matters to us. This Privacy Policy explains what information
          Action! The Movie Game collects, how we use it, and your rights regarding your data.
        </p>

        <h2 className="legal-page__section">1. Information We Collect</h2>
        <p className="legal-page__subhead">Information you provide:</p>
        <ul className="legal-page__list">
          <li>Email address (used for account creation and authentication)</li>
          <li>Display name / username</li>
          <li>Profile avatar (optional, user-uploaded)</li>
        </ul>
        <p className="legal-page__subhead">Information collected automatically:</p>
        <ul className="legal-page__list">
          <li>Gameplay data (scores, streaks, hop counts, game history)</li>
          <li>Ticket balance and purchase history</li>
          <li>Device type and operating system (for technical support purposes)</li>
        </ul>

        <h2 className="legal-page__section">2. How We Use Your Information</h2>
        <p>We use your information to:</p>
        <ul className="legal-page__list">
          <li>Create and manage your account</li>
          <li>Provide and improve the App experience</li>
          <li>Process in-app purchases and maintain your ticket balance</li>
          <li>Maintain your gameplay statistics and streak history</li>
          <li>Send transactional communications (account-related only — we do not send marketing emails)</li>
          <li>Ensure the security and integrity of the App</li>
        </ul>

        <h2 className="legal-page__section">3. Third-Party Services</h2>
        <p>Action! The Movie Game uses the following third-party services:</p>

        <p>
          <strong>Firebase (Google)</strong> — Authentication, database storage, and app
          infrastructure. Your account data and gameplay statistics are stored in Firebase
          Firestore. Firebase is governed by Google's Privacy Policy.
        </p>
        <p>
          <strong>The Movie Database (TMDB)</strong> — All movie and actor data is sourced from
          TMDB. We do not share your personal data with TMDB.
        </p>
        <p>
          <strong>RevenueCat</strong> — In-app purchase management. RevenueCat processes
          purchase events and manages entitlements. RevenueCat is governed by its own
          Privacy Policy.
        </p>
        <p>
          <strong>Vercel Analytics</strong> — We use Vercel Analytics to collect anonymous,
          aggregate website traffic data including page views and visitor counts. No personally
          identifiable information is collected. Vercel Analytics does not use cookies or
          fingerprinting. See Vercel's Privacy Policy for details.
        </p>
        <p>
          <strong>Apple / Google</strong> — In-app purchases are processed by Apple (iOS/tvOS)
          or Google (Android) and are subject to their respective privacy policies.
        </p>
        <p>
          We do not share your personal data with any third-party artificial intelligence services.
        </p>

        <h2 className="legal-page__section">4. Data Storage &amp; Security</h2>
        <p>
          Your data is stored securely in Google Firebase Firestore. We implement reasonable
          technical and organizational measures to protect your information against unauthorized
          access, loss, or misuse. However, no method of transmission or storage is 100% secure.
        </p>

        <h2 className="legal-page__section">5. Data Retention</h2>
        <p>
          We retain your account data for as long as your account is active. You may request
          deletion of your account and associated data at any time through the Settings screen
          within the App. Upon deletion, your data will be permanently removed within 30 days.
        </p>

        <h2 className="legal-page__section">6. Children's Privacy</h2>
        <p>
          Action! The Movie Game is not directed at children under the age of 13. We do not
          knowingly collect personal information from children under 13. If you believe a child
          under 13 has provided us with personal information, please contact us and we will
          delete it promptly.
        </p>

        <h2 className="legal-page__section">7. Your Rights</h2>
        <p>Depending on your location, you may have the right to:</p>
        <ul className="legal-page__list">
          <li>Access the personal data we hold about you</li>
          <li>Request correction of inaccurate data</li>
          <li>Request deletion of your data</li>
          <li>Withdraw consent where processing is based on consent</li>
        </ul>
        <p>
          To exercise any of these rights, contact us at:{' '}
          <a href="mailto:support@actionthemoviegame.com" className="legal-page__link">
            support@actionthemoviegame.com
          </a>
        </p>

        <h2 className="legal-page__section">8. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of significant
          changes by posting a notice within the App. Continued use of the App after changes are
          posted constitutes acceptance of the updated Policy.
        </p>

        <h2 className="legal-page__section">9. Contact</h2>
        <p>For privacy-related questions or requests:</p>
        <p>
          Email:{' '}
          <a href="mailto:support@actionthemoviegame.com" className="legal-page__link">
            support@actionthemoviegame.com
          </a>
        </p>

        {onBack && (
          <BottomNav items={[
            { key: 'home', icon: IconHome, label: 'Home', variant: 'primary', onClick: onBack },
          ]} />
        )}
      </div>
    </div>
  )
}
